// YERİ: src/app/domains/website/sections/mkt-convert.sections.ts
// Dönüşüm bölümleri: fiyatlandırma, SSS, talep formu.
//
// mkt-faq sayfaya FAQPage JSON-LD'si basar. Bu yalnız Google için değil:
// yapay zekâ arama motorları (ChatGPT, Perplexity, Claude) da yapılandırılmış
// soru-cevabı doğrudan alıntılar. Cevaplar bu yüzden KISA ve KENDİ BAŞINA
// ANLAMLI yazılır — bağlamdan koparıldığında da doğru kalsın.

import { Component, computed, effect, inject, input, OnDestroy, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { SiteConfig, cfg } from '@/app/domains/site/sections/section.model';
import { LanguageStore } from '../core/language.store';
import { SeoService } from '../core/seo.service';
import { WebCmsService } from '../core/web-cms.service';
import { WebDiscount, WebPricing } from '../core/web.models';
import { MktIcon } from './mkt-icon';
import { MktReveal } from './mkt-motion';
import { MktRouterLink, mktLink, resolveMktLink } from './mkt-link';
import { splitLines } from './mkt-price';
import { planFeatureLines } from './mkt-plan-features';
import { MktTrialHint } from './mkt-trial-hint';

// ═══════════════════════════════════════════════════════════════════════════
// FİYATLANDIRMA
// ═══════════════════════════════════════════════════════════════════════════

interface Plan {
  name?: string;
  description?: string;
  priceMonthly?: string;
  priceYearly?: string;
  priceNote?: string;
  features?: string | string[];
  ctaText?: string;
  ctaUrl?: string;
  /** Kayıt adresine ?plan= olarak taşınır. Elle yazılmış paketlerde boş. */
  planCode?: string;
  highlighted?: string;
  badge?: string;
}

@Component({
  selector: 'mkt-pricing',
  standalone: true,
  imports: [RouterLink, MktIcon, MktReveal, MktTrialHint],
  host: { class: 'block bg-[color:var(--mkt-paper)]' },
  template: `
    <div class="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32">

      <div class="mx-auto max-w-2xl text-center" mktReveal>
        @if (eyebrow()) {
          <p class="mkt-eyebrow justify-center">{{ eyebrow() }}</p>
        }
        @if (lead()) {
          <h1 class="mkt-h2 mt-4 text-3xl sm:text-[2.75rem]/[1.18]">{{ title() }}</h1>
        } @else {
          <h2 class="mkt-h2 mt-4 text-3xl sm:text-[2.75rem]/[1.18]">{{ title() }}</h2>
        }
        @if (subtitle()) {
          <p class="mkt-lead mt-5">{{ subtitle() }}</p>
        }

        @if (currencies().length > 1) {
          <!-- Para birimi seçici. Yalnız birden fazla para birimi YAYINDAYSA
               çıkar; tek seçenekli bir liste ziyaretçiye hiçbir şey söylemez.
               Seçim URL'e yazılıyor (?currency=USD) — paylaşılan bağlantı
               karşı tarafta da aynı fiyatı gösterir. -->
          <div class="mt-8 inline-flex items-center gap-1 rounded-xl bg-[color:var(--mkt-paper-soft)] p-1 ring-1 ring-inset ring-black/[0.04]">
            @for (option of currencies(); track option.code) {
              <button type="button" (click)="selectCurrency(option.code)"
                [attr.aria-pressed]="option.code === currencyCode()"
                class="rounded-lg px-3 py-1.5 text-[0.8125rem] font-bold transition"
                [class]="option.code === currencyCode()
                  ? 'bg-white text-[color:var(--mkt-accent)] shadow-sm ring-1 ring-black/[0.04]'
                  : 'text-[color:var(--mkt-text-faint)] hover:text-[color:var(--mkt-accent)]'">
                {{ option.code }}
              </button>
            }
          </div>
        }

        @if (showToggle()) {
          <!-- Varsayılan YILLIK: gösterilen fiyat düşük görünsün ve indirim
               rozeti okunsun. -->
          <!-- Anahtar: açık mavi RAY + beyaz kabartma pil.
               Üç tur sürdü. (1) Beyaz pil + açık gri ray: seçili taraf
               görünmüyordu. (2) Siyah pil: görünüyordu ama Mustafa "göz
               yoruyor" dedi — haklı, sayfanın en açık bölümünde tek siyah
               nesneydi. (3) Bu: ray pastel mavi tint, seçili pil beyaz +
               mavi yazı + gölge. Kontrast rengin koyuluğundan değil,
               YÜKSEKLİK farkından geliyor. -->
          <div class="mt-9 inline-flex items-center gap-1 rounded-xl bg-[color:var(--mkt-accent-soft)] p-1 ring-1 ring-inset ring-[color:var(--mkt-accent)]/12"
            role="group" [attr.aria-label]="title()">
            <button type="button" (click)="yearly.set(false)"
              [attr.aria-pressed]="!yearly()"
              class="rounded-lg px-4 py-2 text-sm font-bold transition"
              [class]="!yearly()
                ? 'bg-white text-[color:var(--mkt-accent)] shadow-sm ring-1 ring-black/[0.04]'
                : 'text-[color:var(--mkt-text-muted)] hover:text-[color:var(--mkt-accent)]'">
              {{ monthlyLabel() }}
            </button>
            <button type="button" (click)="yearly.set(true)"
              [attr.aria-pressed]="yearly()"
              class="flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-bold transition"
              [class]="yearly()
                ? 'bg-white text-[color:var(--mkt-accent)] shadow-sm ring-1 ring-black/[0.04]'
                : 'text-[color:var(--mkt-text-muted)] hover:text-[color:var(--mkt-accent)]'">
              {{ yearlyLabel() }}
              @if (yearlyBadge()) {
                <span class="rounded-full px-2 py-0.5 text-[0.6875rem] font-bold text-white"
                  style="background: var(--mkt-gradient)">{{ yearlyBadge() }}</span>
              }
            </button>
          </div>
        }
      </div>

      @if (genelKampanya(); as kampanya) {
        <!-- Genel kampanya seridi. Pakete OZEL kampanyalar burada degil, kendi
             kartinda gorunur; ikisini ayni yerde gostermek "hangisi bana
             uygulanacak" sorusunu doguruyor. -->
        <div class="mx-auto mt-10 flex max-w-2xl items-center justify-center gap-2.5 rounded-xl px-4 py-3 text-sm font-semibold text-white"
          style="background: var(--mkt-gradient)" mktReveal>
          <mkt-icon name="check" [size]="16" class="shrink-0" />
          <span>{{ kampanya.label }}</span>
          @if (kampanya.code) {
            <span class="rounded-md bg-white/20 px-2 py-0.5 font-mono text-[0.75rem] tracking-wide">{{ kampanya.code }}</span>
          }
        </div>
      }

      @if (plans().length === 0) {
        <!-- Paketler veritabanından gelir; API'ye ulaşılamıyorsa boş bir ızgara
             yerine bunu gösteriyoruz. Sessiz boşluk, ziyaretçiye "fiyat yok"
             dedirtir. -->
        <p class="mt-16 text-center text-[color:var(--mkt-text-faint)]">
          {{ unavailableText() }}
        </p>
      }

      <ul class="mt-16 grid items-start gap-6 lg:grid-cols-3">
        @for (plan of plans(); track $index; let i = $index) {
          <li [mktReveal]="i * 70">
            <!-- Öne çıkan paket: gradyan çerçeve. Kalın renkli kenarlık yerine
                 gradyanlı bir "kabuk" — daha pahalı ve daha sakin durur. -->
            <div class="relative h-full rounded-2xl"
              [style.background]="isHighlighted(plan) ? 'var(--mkt-gradient)' : ''"
              [style.padding]="isHighlighted(plan) ? '1.5px' : '0'">

              <div class="flex h-full flex-col rounded-[calc(1rem-1px)] p-7"
                [class]="isHighlighted(plan)
                  ? 'bg-[color:var(--mkt-surface)] shadow-2xl shadow-[color:var(--mkt-accent)]/12'
                  : 'mkt-card'">

                @if (plan.badge) {
                  <span class="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full px-3 py-1 text-[0.6875rem] font-bold uppercase tracking-wide text-white"
                    style="background: var(--mkt-gradient)">
                    {{ plan.badge }}
                  </span>
                }

                <h3 class="text-lg font-bold tracking-tight">{{ plan.name }}</h3>
                @if (plan.description) {
                  <p class="mt-1.5 text-[0.8125rem] text-[color:var(--mkt-text-faint)]">{{ plan.description }}</p>
                }

                @if (hasPackageBadge()) {
                  <!-- Rozet fiyatin USTUNDE: ziyaretci once indirimi, sonra
                       sayiyi okur. Fiyatin KENDISI dusurulmuyor — indirim odeme
                       aninda fatura adresine gore cozuluyor; burada hesaplanan
                       bir sayi kasadakiyle ayrisabilirdi.

                       Kampanyasiz kartlarda rozet GORUNMEZ ama yerini korur:
                       aksi halde kampanyali kartin icerigi asagi kayiyor ve
                       fiyatlar ayni hizada durmuyordu. Hic paket kampanyasi
                       yoksa bu blok hic cizilmez, olu bosluk kalmaz. -->
                  <span class="mt-3 inline-flex w-fit items-center gap-1.5 rounded-full bg-[color:var(--mkt-accent-soft)] px-2.5 py-1 text-[0.6875rem] font-bold text-[color:var(--mkt-accent)]"
                    [class.invisible]="!packageCampaign(plan)">
                    <mkt-icon name="check" [size]="12" class="shrink-0" />
                    {{ packageCampaign(plan)?.label || '—' }}
                  </span>
                }

                <p class="mt-7 flex items-baseline gap-1.5">
                  <!-- Yıllıkta üstü çizili aylık fiyat: anahtar SADECE "Growth"
                       planında sayıyı değiştiriyor ($0 ve "Custom" iki tarafta
                       da aynı). İndirim görünmezse anahtar bozuk sanılıyor. -->
                  @if (strikePriceOf(plan); as struck) {
                    <span class="text-xl font-semibold tracking-tight text-[color:var(--mkt-text-faint)] line-through">{{ struck }}</span>
                  }
                  <span class="text-[2.75rem] font-bold tracking-tight">{{ priceOf(plan) }}</span>
                  @if (plan.priceNote) {
                    <span class="text-sm text-[color:var(--mkt-text-faint)]">{{ plan.priceNote }}</span>
                  }
                </p>

                <!-- Faturalama dönemi YALNIZ dönemin fiyatı gerçekten
                     değiştirdiği paketlerde yazılır. "Custom" bir pakete
                     "Billed yearly" demek yanlış olurdu; ücretsiz pakette de
                     anlamsız. -->
                @if (billingNoteOf(plan); as note) {
                  <p class="mt-1.5 text-[0.8125rem] text-[color:var(--mkt-text-faint)]">{{ note }}</p>
                }

                @if (plan.ctaText) {
                  <a [routerLink]="link(plan.ctaUrl)"
                    [queryParams]="planQuery(plan)"
                    class="mkt-btn mt-7 w-full"
                    [class]="isHighlighted(plan) ? 'mkt-btn-primary' : 'mkt-btn-secondary'">
                    {{ plan.ctaText }}
                  </a>
                }

                @if (featuresOf(plan).length) {
                  <ul class="mt-8 flex-1 space-y-3">
                    @for (feature of featuresOf(plan); track $index) {
                      <li class="flex items-start gap-2.5">
                        <mkt-icon name="check" [size]="15"
                          class="mt-1 shrink-0 text-[color:var(--mkt-accent)]" />
                        <span class="text-[0.875rem] leading-relaxed text-[color:var(--mkt-text-muted)]">{{ feature }}</span>
                      </li>
                    }
                  </ul>
                }
              </div>
            </div>
          </li>
        }
      </ul>

      @if (plans().length) {
        <!-- Free trial hint under the plans (separate element, CMS untouched). -->
        <div class="mt-10 flex justify-center"><mkt-trial-hint /></div>
      }

      @if (footnote()) {
        <p class="mt-12 text-center text-sm text-[color:var(--mkt-text-faint)]">{{ footnote() }}</p>
      }
    </div>
  `,
})
export class MktPricingSection {
  readonly config = input.required<SiteConfig>();
  /** Sayfanin ilk bolumuyse baslik h1 basar (bkz. MktHost.lead). */
  readonly lead = input<boolean>(false);

  private langStore = inject(LanguageStore);
  private cms = inject(WebCmsService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  protected yearly = signal(true);

  /**
   * Fiyat kaynağı: 'packages' → veritabanı, başka her şey → CMS'teki liste.
   *
   * Varsayılan 'packages': fiyat tablosunun elle yazıldığı dönemde site
   * "Growth $49" derken veritabanı "Standard 119 CAD" diyordu. Tek gerçek
   * DB olsun; CMS listesi yalnız özel bir sayfa için gerekirse kullanılsın.
   */
  protected source = computed(() => cfg<string>(this.config(), 'source', 'packages'));

  private pricing = signal<WebPricing | null>(null);

  // ── Kampanya rozetleri ─────────────────────────────────────────────────
  // Ayri istek: kampanya cekilemezse fiyatlar yine gorunmeli. Cografya
  // gonderilmiyor — ziyaretcinin fatura adresi bilinmiyor, dolayisiyla yalniz
  // her yerde gecerli kampanyalar doner. Bolgeye ozel bir kampanyayi yanlis
  // ziyaretciye gostermek, kasada karsiligi cikmayan bir soz olurdu.
  private discounts = signal<WebDiscount[]>([]);

  /**
   * Secili faturalama dongusunde gecerli kampanyalar.
   * `billingCycle` dolu bir kural yalniz o dongude sayilir: yillik kampanyayi
   * aylik sekmesinde gostermek uygulanmayacak bir indirim vaat eder.
   */
  private activeCampaigns = computed(() => {
    const cycle = this.yearly() ? 'yearly' : 'monthly';
    return this.discounts().filter(d => !d.billingCycle || d.billingCycle === cycle);
  });

  /** Tum pakete uygulanan en yuksek oranli kampanya (serit icin). */
  protected genelKampanya = computed<WebDiscount | null>(() => {
    const candidates = this.activeCampaigns().filter(d => d.scope === 'all');
    if (!candidates.length) return null;
    // Birden fazlaysa en yuksek deger: motor da odeme aninda musteri lehine
    // olani seciyor, vitrin baska bir sey vaat etmemeli.
    return candidates.reduce((a, b) => (b.value > a.value ? b : a));
  });

  /** Herhangi bir pakette kampanya var mi — rozet serisi icin yer ayrilsin. */
  protected hasPackageBadge = computed(() =>
    this.activeCampaigns().some(d => d.scope === 'package'));

  /** Bu pakete OZEL kampanya (kart rozeti icin). */
  protected packageCampaign(plan: Plan): WebDiscount | null {
    const kod = (plan.planCode ?? '').trim().toUpperCase();
    if (!kod) return null;
    const candidates = this.activeCampaigns().filter(d =>
      d.scope === 'package' && (d.targetPackageCode ?? '').toUpperCase() === kod);
    if (!candidates.length) return null;
    return candidates.reduce((a, b) => (b.value > a.value ? b : a));
  }

  protected currencies = computed(() => this.pricing()?.available ?? []);
  protected currency = computed(() => this.pricing()?.currency ?? null);
  protected currencyCode = computed(() => this.currency()?.code ?? '');

  constructor() {
    effect(() => {
      if (this.source() !== 'packages') return;

      // Para birimi URL'den: paylaşılan bağlantı karşı tarafta da aynı
      // fiyatı göstersin. Yoksa backend varsayılana düşer.
      const wanted = this.route.snapshot.queryParamMap.get('currency') ?? undefined;

      this.cms.getPublicDiscounts().subscribe({
        next: (list) => this.discounts.set(list),
        error: () => this.discounts.set([]),
      });

      this.cms.getPricing(wanted).subscribe({
        next: (data) => this.pricing.set(data),
        // Fiyat çekilemezse CMS'teki listeye düşüyoruz — fiyatsız bir fiyat
        // sayfası, eski fiyatlı bir sayfadan daha kötü.
        error: () => this.pricing.set(null),
      });
    });
  }

  protected selectCurrency(code: string): void {
    if (code === this.currencyCode()) return;

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { currency: code },
      queryParamsHandling: 'merge',
    });

    this.cms.getPricing(code).subscribe({ next: (data) => this.pricing.set(data) });
  }

  protected eyebrow      = computed(() => cfg(this.config(), 'eyebrow', ''));
  protected title        = computed(() => cfg(this.config(), 'title', ''));
  protected subtitle     = computed(() => cfg(this.config(), 'subtitle', ''));
  protected footnote     = computed(() => cfg(this.config(), 'footnote', ''));
  protected unavailableText = computed(() =>
    cfg(this.config(), 'unavailableText', 'Prices are being updated. Please check back shortly.'));
  protected monthlyLabel = computed(() => cfg(this.config(), 'monthlyLabel', 'Monthly'));
  protected yearlyLabel  = computed(() => cfg(this.config(), 'yearlyLabel', 'Yearly'));
  protected yearlyBadge  = computed(() => cfg(this.config(), 'yearlyBadge', ''));

  // Config'ten gelen mantıksal alanlar STRING'dir ('true'/'false').
  protected showToggle = computed(() => cfg<string>(this.config(), 'showToggle', 'true') !== 'false');

  /** Paket koduna göre CMS'teki ad/açıklama çevirisi (source='packages'). */
  private planTexts = computed(() => {
    const raw = this.config()['planTexts'];
    const list: { code?: string; name?: string; description?: string }[] = Array.isArray(raw) ? raw : [];
    return new Map(list
      .filter((t) => (t?.code ?? '').trim())
      .map((t) => [(t.code ?? '').trim().toUpperCase(), { name: (t.name ?? '').trim(), description: (t.description ?? '').trim() }]));
  });

  /** CMS'te elle yazılmış paketler — yalnız source != 'packages' iken. */
  private manualPlans = computed<Plan[]>(() => {
    const raw = this.config()['plans'];
    return Array.isArray(raw) ? raw : [];
  });

  /**
   * Ekranda çizilen paketler.
   *
   * Veritabanından gelen paketler CMS'in Plan biçimine çevriliyor: şablon tek
   * bir yapıyla çalışsın, iki ayrı kart tasarımı bakmak zorunda kalmayalım.
   * Özellik satırları paket LİMİTLERİNDEN türetiliyor (backend) — elle yazılan
   * bir liste, limit değişince sessizce yalan olurdu.
   */
  protected plans = computed<Plan[]>(() => {
    const data = this.pricing();
    if (this.source() !== 'packages' || !data) return this.manualPlans();

    const highlightCode = cfg<string>(this.config(), 'highlightCode', '').toUpperCase();
    const enterpriseText = cfg(this.config(), 'enterprisePriceText', 'Custom');
    const locale = this.langStore.current();
    const texts = this.planTexts();

    return data.plans.map((plan, index) => ({
      // Paket adı ve "kime uygun" cümlesi Package tablosunda TEK dilde duruyor;
      // CMS'teki paket metni (koda göre) varsa o dilde onu yazar.
      name: texts.get(plan.code.toUpperCase())?.name || plan.name,
      description: texts.get(plan.code.toUpperCase())?.description || (plan.description ?? ''),
      priceMonthly: plan.isEnterprise ? enterpriseText : this.money(plan.priceMonthly),
      priceYearly: plan.isEnterprise ? enterpriseText : this.money(plan.priceYearlyMonthly),
      priceNote: plan.isEnterprise ? '' : cfg(this.config(), 'priceNote', '/month'),
      // Sayılar paketten, cümle kalıpları CMS'ten (boş kalıp = bugünkü İngilizce).
      features: plan.limits
        ? planFeatureLines(plan.limits, plan.isEnterprise, this.config(), locale)
        : plan.features,
      ctaText: plan.isEnterprise
        ? cfg(this.config(), 'enterpriseCtaText', 'Talk to sales')
        : cfg(this.config(), 'planCtaText', 'Get started'),
      ctaUrl: plan.isEnterprise
        ? cfg(this.config(), 'enterpriseCtaUrl', 'contact')
        : cfg(this.config(), 'planCtaUrl', '/auth/sign-up'),
      // Ziyaretçi hangi karta bastıysa kayıt formunda ve paket adımında o plan
      // seçili gelsin. Kod taşınmazsa kullanıcı paketi portalda baştan seçmek
      // zorunda kalıyordu. "Sales" kartında plan yok — orada teklif konuşulur.
      planCode: plan.isEnterprise ? undefined : plan.code,
      // Öne çıkan paket koda göre seçilir; kod verilmemişse ortadaki.
      highlighted: String(
        highlightCode
          ? plan.code.toUpperCase() === highlightCode
          : index === Math.floor(data.plans.length / 2),
      ),
      badge: (highlightCode ? plan.code.toUpperCase() === highlightCode : index === Math.floor(data.plans.length / 2))
        ? cfg(this.config(), 'highlightBadge', 'Most popular')
        : undefined,
    }));
  });

  /**
   * Paket kartının kayıt adresine taşıyacağı sorgu parametreleri.
   *
   * DİKKAT: kod ctaUrl'in İÇİNE yazılamaz. routerLink yol parçalarını
   * URL-encode eder; '?' işareti '%3F' olur ve adres 404'e düşer — bu dosyada
   * '#' ile bir kez yaşandı (bkz. mkt-link.ts). Sorgu AYRI verilmek zorunda.
   *
   * Dış bağlantıda ve kodu olmayan elle yazılmış paketlerde null döner:
   * routerLink dış URL'i href gibi kullanır, oraya queryParams eklemek anlamsız.
   */
  protected planQuery(plan: Plan): Record<string, string> | null {
    const code = (plan.planCode ?? '').trim();
    if (!code) return null;

    const url = (plan.ctaUrl ?? '').trim();
    if (!url || /^https?:\/\/|^mailto:|^tel:/i.test(url)) return null;

    return { plan: code.toUpperCase() };
  }

  /**
   * Fiyatı para birimiyle yazar.
   *
   * Simge ve konumu para biriminden geliyor ($69 ama 69 ₺). Tam yerelleştirmeyi
   * (binlik ayracı) tarayıcının Intl'ine bırakıyoruz; küsurat gösterilmiyor —
   * paket fiyatları tam sayı ve "69.00" pazarlama sayfasında gereksiz gürültü.
   */
  private money(value?: number | null): string {
    if (value == null) return '';

    const currency = this.currency();
    // Yerel ayar ZİYARETÇİNİN DİLİNDEN: sabit 'en-US' binlik ayracını her
    // dilde İngilizce basıyordu (1,299 — Almanca'da doğrusu 1.299). Yorum
    // zaten "Intl'e bırakıyoruz" diyordu ama kod dili sabitliyordu.
    const amount = new Intl.NumberFormat(this.langStore.current(), { maximumFractionDigits: 0 }).format(value);
    if (!currency) return amount;

    return currency.symbolBefore ? `${currency.symbol}${amount}` : `${amount} ${currency.symbol}`;
  }

  protected isHighlighted(plan: Plan): boolean {
    return String(plan.highlighted) === 'true';
  }

  protected monthlyNote = computed(() => cfg(this.config(), 'monthlyNote', 'Billed monthly'));
  protected yearlyNote  = computed(() => cfg(this.config(), 'yearlyNote', 'Billed yearly'));

  /** Dönem notu — yalnız iki fiyatı farklı olan (yani dönemin gerçekten fark
      ettiği) paketlerde. */
  protected billingNoteOf(plan: Plan): string {
    if (!this.showToggle()) return '';

    const monthly = (plan.priceMonthly ?? '').trim();
    const yearly = (plan.priceYearly ?? '').trim();
    if (!monthly || !yearly || monthly === yearly) return '';

    return this.yearly() ? this.yearlyNote() : this.monthlyNote();
  }

  /** Yıllık fiyat girilmemişse anahtar bu planı etkilemez. */
  protected priceOf(plan: Plan): string {
    const yearly = (plan.priceYearly ?? '').trim();
    return this.yearly() && yearly ? yearly : (plan.priceMonthly ?? '').trim();
  }

  /**
   * Yıllık seçiliyken gösterilecek üstü çizili aylık fiyat — yalnız iki fiyat
   * GERÇEKTEN farklıysa. Aynıysa (ücretsiz plan, "Custom") boş döner; üstü
   * çizili aynı sayıyı göstermek indirim varmış gibi okunur, yani yalan olur.
   */
  protected strikePriceOf(plan: Plan): string {
    if (!this.yearly()) return '';

    const monthly = (plan.priceMonthly ?? '').trim();
    const yearly = (plan.priceYearly ?? '').trim();

    return yearly && monthly && yearly !== monthly ? monthly : '';
  }

  protected featuresOf(plan: Plan): string[] {
    return splitLines(plan.features);
  }

  protected link(url?: string): MktRouterLink {
    return resolveMktLink(url ?? '', this.langStore.current());
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// SSS — FAQPage JSON-LD üretir (Google + yapay zekâ arama motorları)
// ═══════════════════════════════════════════════════════════════════════════

@Component({
  selector: 'mkt-faq',
  standalone: true,
  imports: [RouterLink, MktReveal],
  host: { class: 'block border-t border-[color:var(--mkt-border)] bg-[color:var(--mkt-paper-soft)]' },
  template: `
    <div class="mx-auto max-w-3xl px-5 py-24 sm:px-8 lg:py-32">

      <div class="text-center" mktReveal>
        @if (lead()) {
          <h1 class="mkt-h2 text-3xl sm:text-[2.5rem]/[1.18]">{{ title() }}</h1>
        } @else {
          <h2 class="mkt-h2 text-3xl sm:text-[2.5rem]/[1.18]">{{ title() }}</h2>
        }
        @if (subtitle()) {
          <p class="mkt-lead mt-5">{{ subtitle() }}</p>
        }
      </div>

      <!-- <details> kullanıyoruz: JS olmadan da açılır-kapanır ve cevap HTML'de
           HER ZAMAN vardır. Kendi accordion'umuz kapalı içeriği DOM'dan
           çıkarsaydı ne Google ne de bir yapay zekâ cevapları görebilirdi. -->
      <div class="mt-14 space-y-3">
        @for (item of visibleItems(); track $index) {
          <details class="mkt-card group px-6 py-5" [mktReveal]="$index * 35">
            <summary class="flex cursor-pointer items-center justify-between gap-4 text-start text-[1.0625rem] font-semibold [&::-webkit-details-marker]:hidden">
              {{ item.question }}
              <span class="grid size-7 shrink-0 place-items-center rounded-lg border border-[color:var(--mkt-border)] text-[color:var(--mkt-text-faint)] transition duration-200 group-open:rotate-45 group-open:border-transparent group-open:bg-[color:var(--mkt-accent)] group-open:text-white">
                <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor"
                  stroke-width="2" stroke-linecap="round" aria-hidden="true">
                  <path d="M10 4v12M4 10h12" />
                </svg>
              </span>
            </summary>
            <p class="mt-4 leading-relaxed text-[color:var(--mkt-text-muted)]">{{ item.answer }}</p>
          </details>
        }
      </div>

      @if (ctaText()) {
        <!-- Ana sayfada yalnız birkaç soru duruyor; gerisi kendi sayfasında.
             Uzun bir SSS listesi ana sayfanın altını şişiriyor ve asıl
             çağrıyı (kayıt) aşağı itiyordu. -->
        <div class="mt-10 text-center">
          <a [routerLink]="ctaLink()" class="mkt-btn mkt-btn-secondary">
            {{ ctaText() }}
          </a>
        </div>
      }
    </div>
  `,
})
export class MktFaqSection implements OnInit, OnDestroy {
  readonly config = input.required<SiteConfig>();
  /** Sayfanin ilk bolumuyse baslik h1 basar (bkz. MktHost.lead). */
  readonly lead = input<boolean>(false);

  private seo = inject(SeoService);

  protected title    = computed(() => cfg(this.config(), 'title', ''));
  protected subtitle = computed(() => cfg(this.config(), 'subtitle', ''));

  protected ctaText = computed(() => cfg(this.config(), 'ctaText', ''));
  protected ctaLink = mktLink(() => cfg(this.config(), 'ctaUrl', ''));

  protected items = computed<{ question?: string; answer?: string }[]>(() => {
    const raw = this.config()['items'];
    return Array.isArray(raw) ? raw : [];
  });

  /** 0 / boş → hepsi. Ana sayfada 3, SSS sayfasında sınırsız. */
  private maxItems = computed(() => {
    const raw = Number(cfg<string>(this.config(), 'maxItems', ''));
    return Number.isFinite(raw) && raw > 0 ? raw : 0;
  });

  protected visibleItems = computed(() => {
    const limit = this.maxItems();
    return limit > 0 ? this.items().slice(0, limit) : this.items();
  });

  ngOnInit(): void {
    // JSON-LD GÖRÜNEN sorulardan üretilir, tam listeden değil: Google, FAQ
    // işaretlemesinin sayfada görünen içerikle birebir eşleşmesini şart koşar.
    // Gizlenen soruları da yazsaydık işaretleme geçersiz sayılırdı.
    const items = this.visibleItems().filter((i) => i.question && i.answer);
    if (items.length === 0) return;

    this.seo.setJsonLd('faq', {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: items.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: { '@type': 'Answer', text: item.answer },
      })),
    });
  }

  ngOnDestroy(): void {
    this.seo.setJsonLd('faq', null);
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// TALEP FORMU
// ═══════════════════════════════════════════════════════════════════════════

@Component({
  selector: 'mkt-lead-form',
  standalone: true,
  imports: [FormsModule, MktIcon, MktReveal],
  host: { class: 'mkt-dark relative isolate block overflow-hidden mkt-glow' },
  template: `
    <div class="relative z-10 mx-auto grid max-w-7xl gap-14 px-5 py-24 sm:px-8 lg:grid-cols-2 lg:gap-20 lg:py-32">

      <div mktReveal>
        @if (eyebrow()) {
          <p class="mkt-eyebrow">{{ eyebrow() }}</p>
        }
        @if (lead()) {
          <h1 class="mkt-h2 mt-4 text-3xl sm:text-[2.75rem]/[1.18]">{{ title() }}</h1>
        } @else {
          <h2 class="mkt-h2 mt-4 text-3xl sm:text-[2.75rem]/[1.18]">{{ title() }}</h2>
        }
        @if (subtitle()) {
          <p class="mkt-lead mt-5">{{ subtitle() }}</p>
        }

        @if (bullets().length) {
          <ul class="mt-9 space-y-3.5">
            @for (bullet of bullets(); track $index) {
              <li class="flex items-start gap-3">
                <span class="mt-0.5 grid size-5 shrink-0 place-items-center rounded-full bg-white/10">
                  <mkt-icon name="check" [size]="12" class="text-[color:var(--mkt-accent-glow)]" />
                </span>
                <span class="text-[color:var(--mkt-text-muted)]">{{ bullet }}</span>
              </li>
            }
          </ul>
        }
      </div>

      <div [mktReveal]="100">
        @if (sent()) {
          <div class="grid h-full min-h-80 place-items-center rounded-2xl border border-white/10 bg-white/5 p-10 text-center backdrop-blur">
            <div>
              <span class="mx-auto grid size-14 place-items-center rounded-2xl text-white"
                style="background: var(--mkt-gradient)">
                <mkt-icon name="check" [size]="26" />
              </span>
              <p class="mt-5 text-xl font-bold tracking-tight">{{ successTitle() }}</p>
              <p class="mt-2 text-[color:var(--mkt-text-muted)]">{{ successText() }}</p>
            </div>
          </div>
        } @else {
          <form class="relative rounded-2xl border border-white/10 bg-white/[0.04] p-7 backdrop-blur"
            (ngSubmit)="submit()">

            <!-- Bal küpü: insan görmez, bot doldurur. Backend dolu gelirse
                 isteği başarılı SAYAR ama kaydetmez. -->
            <div class="pointer-events-none absolute -left-[9999px] top-0 opacity-0" aria-hidden="true">
              <label for="mkt-website">Website</label>
              <input id="mkt-website" type="text" tabindex="-1" autocomplete="off"
                [(ngModel)]="form.website" name="website" />
            </div>

            <div class="grid gap-4 sm:grid-cols-2">
              <label class="block">
                <span class="mb-1.5 block text-[0.8125rem] font-medium text-[color:var(--mkt-text-muted)]">{{ nameLabel() }} *</span>
                <input type="text" required name="fullName" [(ngModel)]="form.fullName" class="mkt-field" />
              </label>
              <label class="block">
                <span class="mb-1.5 block text-[0.8125rem] font-medium text-[color:var(--mkt-text-muted)]">{{ emailLabel() }} *</span>
                <input type="email" required name="email" [(ngModel)]="form.email" class="mkt-field" />
              </label>
              <label class="block">
                <span class="mb-1.5 block text-[0.8125rem] font-medium text-[color:var(--mkt-text-muted)]">{{ companyLabel() }}</span>
                <input type="text" name="companyName" [(ngModel)]="form.companyName" class="mkt-field" />
              </label>
              <label class="block">
                <span class="mb-1.5 block text-[0.8125rem] font-medium text-[color:var(--mkt-text-muted)]">{{ phoneLabel() }}</span>
                <input type="tel" name="phone" [(ngModel)]="form.phone" class="mkt-field" />
              </label>
            </div>

            <label class="mt-4 block">
              <span class="mb-1.5 block text-[0.8125rem] font-medium text-[color:var(--mkt-text-muted)]">{{ messageLabel() }}</span>
              <textarea rows="4" name="message" [(ngModel)]="form.message" class="mkt-field"></textarea>
            </label>

            @if (error()) {
              <p class="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-sm text-red-300">
                {{ error() }}
              </p>
            }

            <button type="submit" [disabled]="sending()" class="mkt-btn mkt-btn-primary mt-6 w-full disabled:opacity-60">
              {{ sending() ? sendingText() : submitText() }}
              @if (!sending()) {
                <mkt-icon name="arrow" [size]="16" />
              }
            </button>

            @if (privacyText()) {
              <p class="mt-4 text-center text-xs text-[color:var(--mkt-text-faint)]">{{ privacyText() }}</p>
            }
          </form>
        }
      </div>
    </div>
  `,
  styles: [`
    .mkt-field {
      width: 100%;
      border-radius: 0.75rem;
      border: 1px solid rgb(255 255 255 / 0.12);
      background: rgb(255 255 255 / 0.04);
      padding: 0.625rem 0.875rem;
      font-size: 0.9375rem;
      color: inherit;
      outline: none;
      transition: border-color 150ms ease, box-shadow 150ms ease;
    }

    .mkt-field::placeholder {
      color: var(--mkt-text-faint);
    }

    .mkt-field:focus {
      border-color: var(--mkt-accent-glow);
      box-shadow: 0 0 0 4px rgb(129 140 248 / 0.15);
    }
  `],
})
export class MktLeadFormSection {
  readonly config = input.required<SiteConfig>();
  /** Sayfanin ilk bolumuyse baslik h1 basar (bkz. MktHost.lead). */
  readonly lead = input<boolean>(false);

  private cms = inject(WebCmsService);
  private langStore = inject(LanguageStore);

  protected sending = signal(false);
  protected sent = signal(false);
  protected error = signal('');

  protected form = {
    fullName: '',
    email: '',
    companyName: '',
    phone: '',
    message: '',
    website: '',
  };

  protected eyebrow      = computed(() => cfg(this.config(), 'eyebrow', ''));
  protected title        = computed(() => cfg(this.config(), 'title', ''));
  protected subtitle     = computed(() => cfg(this.config(), 'subtitle', ''));
  protected nameLabel    = computed(() => cfg(this.config(), 'nameLabel', 'Full name'));
  protected emailLabel   = computed(() => cfg(this.config(), 'emailLabel', 'Work email'));
  protected companyLabel = computed(() => cfg(this.config(), 'companyLabel', 'Company'));
  protected phoneLabel   = computed(() => cfg(this.config(), 'phoneLabel', 'Phone'));
  protected messageLabel = computed(() => cfg(this.config(), 'messageLabel', 'How can we help?'));
  protected submitText   = computed(() => cfg(this.config(), 'submitText', 'Request a demo'));
  protected sendingText  = computed(() => cfg(this.config(), 'sendingText', 'Sending…'));
  protected successTitle = computed(() => cfg(this.config(), 'successTitle', 'Thank you!'));
  protected successText  = computed(() => cfg(this.config(), 'successText', 'We will get back to you shortly.'));
  protected privacyText  = computed(() => cfg(this.config(), 'privacyText', ''));
  protected source       = computed(() => cfg(this.config(), 'source', 'contact-form'));
  protected errorText    = computed(() => cfg(this.config(), 'errorText', 'Something went wrong. Please try again.'));

  protected bullets = computed(() => splitLines(this.config()['bullets']));

  protected submit(): void {
    if (this.sending()) return;

    this.error.set('');
    this.sending.set(true);

    this.cms
      .submitLead({
        fullName: this.form.fullName,
        email: this.form.email,
        companyName: this.form.companyName,
        phone: this.form.phone,
        message: this.form.message,
        website: this.form.website,
        source: this.source(),
        pageSlug: typeof location !== 'undefined' ? location.pathname : '',
        languageCode: this.langStore.current(),
      })
      .subscribe({
        next: (response) => {
          this.sending.set(false);
          if (response.success) this.sent.set(true);
          // Backend doğrulama mesajları Türkçe ("Ad soyad zorunlu.") — ziyaretçi
          // sayfanın dilinde okumalı. Metin CMS'ten (errorText), dil başına.
          else this.error.set(this.errorText());
        },
        error: () => {
          this.sending.set(false);
          this.error.set(this.errorText());
        },
      });
  }
}
